import { ButtonTypes } from "devextreme-react/button";
import { Template } from "devextreme-react/core/template";
import Drawer from "devextreme-react/drawer";
import { TreeViewTypes } from "devextreme-react/tree-view";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { AppHeader, SideNavigationMenu } from "../../components";
import { SideNavigationItem } from "../../components/side-navigation-menu/SideNavigationMenu";
import { useScreenSize } from "../../utils/media-query";
import { useMenuPatch } from "../../utils/patches";
import "./side-nav-outer-toolbar.scss";

enum MenuOpenState {
  Closed = 1,
  Opened = 2,
  TemporaryOpened = 3,
}

type MenuStatus = MenuOpenState | null;

export const SideNavOuterToolbar = ({ children }: React.PropsWithChildren) => {
  const navigate = useNavigate();
  const { isXSmall, isLarge } = useScreenSize();
  const [patchCssClass, onMenuReady] = useMenuPatch();
  const [menuStatus, setMenuStatus] = useState<MenuStatus>(null);

  const getDefaultMenuOpenState = useCallback(
    () => (isLarge ? MenuOpenState.Opened : MenuOpenState.Closed),
    [isLarge],
  );
  const getMenuOpenState = useCallback(
    (status: MenuStatus) => {
      if (status === null) {
        return getDefaultMenuOpenState();
      }

      return status;
    },
    [getDefaultMenuOpenState],
  );

  const getMenuStatus = useCallback(
    (status: MenuStatus) => {
      return status === getDefaultMenuOpenState() ? null : status;
    },
    [getDefaultMenuOpenState],
  );

  const changeMenuStatus = useCallback(
    (reducerFn: (prevStatus: MenuStatus) => MenuStatus) => {
      setMenuStatus((prevMenuStatus) =>
        getMenuStatus(
          reducerFn(getMenuOpenState(prevMenuStatus)) ?? prevMenuStatus,
        ),
      );
    },
    [getMenuOpenState, getMenuStatus],
  );

  const toggleMenu = useCallback(
    ({ event }: ButtonTypes.ClickEvent) => {
      changeMenuStatus((prevStatus) =>
        prevStatus === MenuOpenState.Closed
          ? MenuOpenState.Opened
          : MenuOpenState.Closed,
      );
      event?.stopPropagation();
    },
    [changeMenuStatus],
  );

  const temporaryOpenMenu = useCallback(() => {
    changeMenuStatus((prevStatus) =>
      prevStatus === MenuOpenState.Closed
        ? MenuOpenState.TemporaryOpened
        : null,
    );
  }, [changeMenuStatus]);

  const onOutsideClick = useCallback(() => {
    changeMenuStatus((prevStatus) =>
      prevStatus !== MenuOpenState.Closed && !isLarge
        ? MenuOpenState.Closed
        : null,
    );
    return !isLarge;
  }, [isLarge, changeMenuStatus]);

  const onNavigationChanged = useCallback(
    ({
      itemData: { path },
      event,
      node,
    }: TreeViewTypes.ItemClickEvent & { itemData: SideNavigationItem }) => {
      if (
        getMenuOpenState(menuStatus) === MenuOpenState.Closed ||
        !path ||
        node?.selected
      ) {
        event?.preventDefault();
        return;
      }

      navigate(path);
      if (!isLarge || menuStatus === MenuOpenState.TemporaryOpened) {
        setMenuStatus(getMenuStatus(MenuOpenState.Closed));
        event?.stopPropagation();
      }
    },
    [getMenuOpenState, menuStatus, navigate, isLarge, getMenuStatus],
  ) as (e: TreeViewTypes.ItemClickEvent) => void;

  useEffect(() => {
    changeMenuStatus(() => menuStatus);
  }, [changeMenuStatus, menuStatus]);

  return (
    <div className="side-nav-outer-toolbar">
      <AppHeader
        className="layout-header"
        menuToggleEnabled
        toggleMenu={toggleMenu}
        title="invenio HR"
      />
      <Drawer
        className={["drawer layout-body", patchCssClass].join(" ")}
        position="before"
        closeOnOutsideClick={onOutsideClick}
        openedStateMode={isLarge ? "shrink" : "overlap"}
        revealMode={isXSmall ? "slide" : "expand"}
        minSize={isXSmall ? 0 : 48}
        maxSize={250}
        shading={isLarge ? false : true}
        opened={
          getMenuOpenState(menuStatus) === MenuOpenState.Closed ? false : true
        }
        template="menu"
      >
        <div className="content">
          {React.Children.map(children, (item) => {
            return React.isValidElement(item) && item;
          })}
        </div>
        <Template name="menu">
          <SideNavigationMenu
            compactMode={getMenuOpenState(menuStatus) === MenuOpenState.Closed}
            selectedItemChanged={onNavigationChanged}
            openMenu={temporaryOpenMenu}
            onMenuReady={onMenuReady}
          />
        </Template>
      </Drawer>
    </div>
  );
};
